import type { RowData } from "@tanstack/react-table";
import type { DataGridRow } from "../columns/types";
import { getCell, getListEditableCell } from "../editing/cellAccess";
import { openListEditor } from "../editing/list/listEditActions";
import type { DataGridInstance } from "../types";
import type { GridController } from "./gridController";
import type { FocusedRow, GridActionInput } from "./gridMoves";
import type { GridKeyAction } from "./keyMap";
import { enterCell, openRow } from "./leaveActions";
import {
    getListNavigationTarget,
    type ListNavigationKey,
    type ListNavigationTarget,
} from "./listNavigation";
import {
    findFirstFocusableRowIndex,
    findFocusableRowIndex,
    getFocusedAddress,
    getListNavigableColumnIds,
} from "./navigation";
import { focusTableRow } from "./useAutoFocus";

const toListKey = (action: GridKeyAction): ListNavigationKey | null => {
    switch (action.type) {
        case "move":
            return action.direction;
        case "rowStart":
        case "rowEnd":
            return action.type;
        default:
            return null;
    }
};

interface ListCursor<TData extends RowData> {
    input: GridActionInput<TData>;
    rows: ReadonlyArray<DataGridRow<TData>>;
    rowIndex: number;
    row: DataGridRow<TData>;
}

const applyTarget = <TData extends RowData>(
    { input, rows, rowIndex, row }: ListCursor<TData>,
    target: ListNavigationTarget,
) => {
    const { instance, controller } = input;
    switch (target.type) {
        case "stay":
            return true;
        case "expand":
            controller.skipExpandFade();
            row.toggleExpanded(true);
            return true;
        case "row":
            focusTableRow({ instance, controller, rowId: row.id });
            return true;
        case "cell": {
            const index = findFocusableRowIndex({
                rows,
                isSeparator: instance.rowKinds.isSeparator,
                rowIndex: target.rowIndex,
                fromIndex: rowIndex,
            });
            const targetRow = rows[index];
            if (targetRow) {
                focusTableRow({
                    instance,
                    controller,
                    rowId: targetRow.id,
                    columnId: target.columnId,
                });
            }
            return true;
        }
    }
};

/** Enter on a focused cell: into its controls, else open the row from that cell. */
const openFromCell = <TData extends RowData>(
    { input, row }: ListCursor<TData>,
    columnId: string,
) =>
    enterCell(input.target) ||
    openRow({ instance: input.instance, row, columnId });

/** Whether the row allows editing the cell in place. */
const isEditableCell = <TData extends RowData>({
    instance,
    rowElement,
    cellElement,
}: {
    instance: DataGridInstance<TData>;
    rowElement: HTMLElement;
    cellElement: Element;
}) => {
    const rowId = rowElement.dataset.rowId;
    const columnId = cellElement.getAttribute("data-column-id");
    return (
        rowId !== undefined &&
        columnId !== null &&
        getListEditableCell(
            instance,
            getCell(instance.table, { rowId, columnId }),
        ) !== null
    );
};

const CLOSED_TRIGGER_SELECTOR =
    "[data-dt-list-trigger]:not([aria-expanded='true'])";

/** Whether the grid's focus state is on this cell, not on its row. */
const isGridOnCell = <TData extends RowData>({
    instance,
    rowElement,
    cell,
}: {
    instance: DataGridInstance<TData>;
    rowElement: HTMLElement;
    cell: HTMLElement | null;
}) => {
    const current = getFocusedAddress(instance.table.atoms.cellSelection.get());
    return (
        instance.atoms.listCellFocus.get() &&
        current?.rowId === rowElement.dataset.rowId &&
        current?.columnId === cell?.dataset.columnId
    );
};

/**
 * The cell of a closed editor's trigger that just took focus, when the grid
 * is on that cell. A popup returns focus to its trigger only after its exit
 * animation, so by then the user may have moved on to another row.
 */
const getClosedTriggerCell = <TData extends RowData>({
    instance,
    rowElement,
    target,
}: {
    instance: DataGridInstance<TData>;
    rowElement: HTMLElement;
    target: HTMLElement;
}) => {
    const cell = target.matches(CLOSED_TRIGGER_SELECTOR)
        ? target.closest<HTMLElement>("[role='gridcell']")
        : null;
    return isGridOnCell({ instance, rowElement, cell }) ? cell : null;
};

/**
 * List editing's part of the grid's focusin: a focused cell sets
 * `listCellFocus` and a focused row clears it. Focus on a closed editor's
 * trigger while the grid is on its cell moves to the cell. A pointer press
 * that focused a cell with no editor here (focus is quiet) hands focus to its
 * row instead, so a click on a read-only cell opens the row and leaves the row
 * focused, unless the grid put itself on the cell first (a toggle's commit).
 * Returns false when focus moved on, so the caller skips this event.
 */
export const syncListFocus = <TData extends RowData>({
    instance,
    controller,
    rowElement,
    target,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    rowElement: HTMLElement;
    target: EventTarget;
}) => {
    if (!(target instanceof HTMLElement)) {
        return true;
    }
    const triggerCell = getClosedTriggerCell({ instance, rowElement, target });
    if (triggerCell) {
        triggerCell.focus({ preventScroll: true });
        return false;
    }
    const role = target.getAttribute("role");
    if (
        role === "gridcell" &&
        controller.getFocusQuiet() !== null &&
        !isEditableCell({ instance, rowElement, cellElement: target }) &&
        !isGridOnCell({ instance, rowElement, cell: target })
    ) {
        rowElement.focus({ preventScroll: true });
        return false;
    }
    if (role === "gridcell" || role === "row") {
        instance.atoms.listCellFocus.set(role === "gridcell");
    }
    return true;
};

/**
 * List editing keys that differ from row mode: stepping between the focused
 * row and its cells, moving between cells, and Enter on a cell. Returns
 * `null` for every other key, so row mode handles it.
 */
export const runListAction = <TData extends RowData>(
    input: GridActionInput<TData> & { focused: FocusedRow<TData> },
): boolean | null => {
    const { action, instance, controller, focused } = input;
    const { table } = instance;
    const { current, rows, rowIndex } = focused;
    const row = rows[rowIndex];
    if (!current || !row) {
        return null;
    }
    const cursor: ListCursor<TData> = { input, rows, rowIndex, row };
    const isCellFocused = instance.atoms.listCellFocus.get();
    if (action.type === "open") {
        if (!isCellFocused) {
            return null;
        }
        return (
            openListEditor({ instance, controller }, current) ||
            openFromCell(cursor, current.columnId)
        );
    }
    const key = toListKey(action);
    const target =
        key &&
        getListNavigationTarget({
            key,
            rowIndex,
            columnId: isCellFocused ? current.columnId : null,
            columnIds: getListNavigableColumnIds(table),
            rowCount: rows.length,
            firstRowIndex: findFirstFocusableRowIndex(
                rows,
                instance.rowKinds.isSeparator,
            ),
            canExpand: row.getCanExpand(),
            isExpanded: row.getIsExpanded(),
        });
    return target ? applyTarget(cursor, target) : null;
};
